import { useEffect } from 'react';
import { Wordmark } from './ui';

// Static legal pages served at /privacy and /terms. App.tsx detects the
// pathname and renders the appropriate component instead of the game.
// Content is intentionally plain HTML — no game state, no auth, no
// network — so these pages are accessible even if the API is down.

const EFFECTIVE_DATE = 'May 9, 2026';
const CONTACT_EMAIL = 'support@hexponents.com';
const COMPANY = 'DosSantos Systems LLC';

interface LegalLayoutProps {
  title: string;
  children: React.ReactNode;
}

function LegalLayout({ title, children }: LegalLayoutProps) {
  // Set the document title + scroll to top on mount so deep-linking
  // /privacy from elsewhere doesn't drop the user mid-page.
  useEffect(() => {
    document.title = `${title} · Hexponents`;
    window.scrollTo(0, 0);
  }, [title]);

  return (
    <div className="hm-legal-page">
      <header className="hm-legal-header">
        <a href="/" className="hm-legal-home" aria-label="Hexponents home">
          <Wordmark />
        </a>
      </header>
      <main className="hm-legal-main">
        <h1 className="hm-legal-title">{title}</h1>
        <p className="hm-legal-effective">Effective {EFFECTIVE_DATE}</p>
        {children}
        <hr className="hm-legal-divider" />
        <p className="hm-legal-footnote">
          Questions? Email <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
        </p>
        <nav className="hm-legal-nav">
          <a href="/">Back to game</a>
          {' · '}
          <a href="/privacy">Privacy</a>
          {' · '}
          <a href="/terms">Terms</a>
        </nav>
      </main>
    </div>
  );
}

export function PrivacyPolicy() {
  return (
    <LegalLayout title="Privacy Policy">
      <p>
        Hexponents is a daily logic puzzle operated by {COMPANY} ("we", "us"). This
        policy explains what we collect, why, and what we do with it. We collect the
        minimum data needed to run the service.
      </p>

      <h2>What we collect</h2>
      <ul>
        <li>
          <strong>Account information</strong> — email address, display name, and a
          hashed password (when you create an email/password account). We never see
          or store your plaintext password.
        </li>
        <li>
          <strong>Sign in with Apple / Google</strong> — when you choose one of
          these, we receive a stable identifier from the provider plus the email
          address linked to your account there. We use these only to look up or
          create your Hexponents account.
        </li>
        <li>
          <strong>Gameplay data</strong> — your daily solves, streaks, mid-puzzle
          progress, and difficulty preferences. These are stored on our servers so
          you can resume puzzles across devices and see your stats.
        </li>
        <li>
          <strong>Subscription and purchase records</strong> — when you upgrade to
          Premium or buy a hint pack, we receive an opaque identifier from Stripe
          (web) or Apple (iOS) that lets us verify your subscription status. We do
          not see, receive, or store your payment card or banking details.
        </li>
        <li>
          <strong>Push notification token</strong> — if you opt in to daily puzzle
          reminders on the iOS app, the system gives us a device token that lets us
          send you a single daily reminder. You can revoke this in iOS Settings at
          any time.
        </li>
        <li>
          <strong>Server logs</strong> — our servers record basic request metadata
          (IP address, request path, user agent, timestamp) for security and
          debugging. These logs are rotated and discarded after roughly 30 days.
        </li>
      </ul>

      <h2>How we use it</h2>
      <ul>
        <li>To deliver today's puzzle and your historical archive.</li>
        <li>To track your streak and stats.</li>
        <li>To verify Premium status and gate paid features.</li>
        <li>To send you the optional daily push reminder (iOS only).</li>
        <li>To respond when you contact support.</li>
        <li>To detect and prevent abuse, fraud, or security incidents.</li>
      </ul>
      <p>
        We do not sell your data. We do not show third-party ads on the website. We
        do not run third-party analytics or tracking scripts.
      </p>

      <h2>Service providers</h2>
      <p>
        We use a small number of third parties to deliver Hexponents. Each only
        receives the data it needs to do its job:
      </p>
      <ul>
        <li>
          <strong>Stripe</strong> — processes web subscription payments and one-time
          hint pack purchases. Stripe handles all card data directly; we receive
          only an opaque customer/subscription identifier.
        </li>
        <li>
          <strong>Apple</strong> — processes iOS in-app purchases (StoreKit) and
          delivers push notifications (APNs). We receive an opaque transaction
          identifier from StoreKit and an opaque device token from APNs.
        </li>
        <li>
          <strong>Google</strong> — verifies your identity if you choose Sign in
          with Google. We receive only the verified email and a stable identifier.
        </li>
        <li>
          <strong>Apple Sign In</strong> — verifies your identity if you choose Sign
          in with Apple. We receive only the verified email (or a private relay
          address if you choose) and a stable identifier.
        </li>
      </ul>

      <h2>Data retention and deletion</h2>
      <p>
        We retain your account and gameplay data while your account exists. To delete
        your account and all associated data, email <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> from
        your account's email address. We will confirm and complete deletion within a
        reasonable time. Some records may persist briefly in encrypted backups before
        being purged on the normal backup rotation.
      </p>

      <h2>Children</h2>
      <p>
        Hexponents is not directed at children under 13, and we do not knowingly
        collect personal information from anyone under 13. If you believe a child
        under 13 has signed up, please contact us and we will remove the account.
      </p>

      <h2>International users</h2>
      <p>
        Our servers are located in the United States. By using Hexponents, you
        understand that your data will be processed and stored in the U.S.
      </p>

      <h2>Changes to this policy</h2>
      <p>
        If we make material changes to this policy, we will update the effective
        date at the top and, where appropriate, notify you in the app. Continued use
        of Hexponents after the effective date means you accept the updated policy.
      </p>

      <h2>Contact</h2>
      <p>
        For privacy questions or to exercise any of the rights above, email{' '}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
      </p>
    </LegalLayout>
  );
}

export function TermsOfService() {
  return (
    <LegalLayout title="Terms of Service">
      <p>
        These terms govern your use of Hexponents, a daily logic puzzle operated by
        {' '}{COMPANY} ("we", "us"). By creating an account or playing the game you
        agree to these terms.
      </p>

      <h2>The service</h2>
      <p>
        Hexponents publishes one new daily puzzle per difficulty (Easy, Medium,
        Hard, Expert). Today's puzzle on Easy, Medium, and Hard is free for everyone.
        The past-day archive and the Expert puzzle each day are gated to active
        Premium subscribers. We may add, remove, or change features at any time.
      </p>

      <h2>Your account</h2>
      <ul>
        <li>You must provide accurate information when creating an account.</li>
        <li>You are responsible for keeping your password (and any linked third-party sign-in) secure.</li>
        <li>One person, one account. Don't share or sell access.</li>
        <li>You must be 13 or older to create an account.</li>
      </ul>

      <h2>Acceptable use</h2>
      <p>You agree not to:</p>
      <ul>
        <li>Probe, scan, or attempt to compromise the service or its infrastructure.</li>
        <li>Scrape or copy puzzles, solutions, or other content for redistribution.</li>
        <li>Use bots, automated tools, or other mechanisms to manipulate streaks, stats, or leaderboards.</li>
        <li>Attempt to bypass paywalls, restrictions, or rate limits.</li>
        <li>Harass other users or our staff.</li>
      </ul>
      <p>
        We may suspend or terminate any account that violates these rules. Severe or
        repeated violations may result in a permanent ban.
      </p>

      <h2>Premium and one-time purchases</h2>
      <ul>
        <li>
          <strong>Premium subscription</strong> ($0.99/month or $9.99/year) unlocks
          past-day puzzles, the daily Expert puzzle, and (on the iOS app) removes ads.
          On the web, billing is processed by Stripe; on iOS, by Apple. Subscriptions
          renew automatically until you cancel.
        </li>
        <li>
          <strong>Hint pack</strong> ($0.99 for 25 hints) is a one-time purchase that
          adds hints to your account balance.
        </li>
        <li>
          <strong>Cancellation</strong> — cancel anytime in your Account view (web)
          or in your Apple ID subscription settings (iOS). You keep Premium until the
          end of the current billing period; we don't pro-rate refunds for partial
          months.
        </li>
        <li>
          <strong>Refunds</strong> — refunds are governed by the platform that
          processed your payment. For iOS purchases, request a refund through Apple
          (reportaproblem.apple.com). For web purchases, contact us at{' '}
          <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> and we will work with
          you in good faith.
        </li>
        <li>
          We may change prices for new subscriptions. We will not change the price
          of an existing subscription without giving you advance notice and a chance
          to cancel.
        </li>
      </ul>

      <h2>Service availability</h2>
      <p>
        We work hard to keep Hexponents up, but we do not promise uninterrupted
        service. Daily puzzles roll over at midnight Eastern Time and are timestamped
        accordingly; if a brief outage prevents a puzzle from publishing, we will
        publish the missed puzzle as soon as possible.
      </p>

      <h2>Disclaimer of warranty</h2>
      <p>
        Hexponents is provided "as is" without warranty of any kind, express or
        implied, including but not limited to warranties of merchantability, fitness
        for a particular purpose, or non-infringement.
      </p>

      <h2>Limitation of liability</h2>
      <p>
        To the maximum extent permitted by law, {COMPANY} is not liable for any
        indirect, incidental, special, consequential, or punitive damages arising
        from your use of Hexponents. Our total liability for any claim is limited to
        the amount you paid us in the twelve months preceding the claim, or US$25,
        whichever is greater.
      </p>

      <h2>Termination</h2>
      <p>
        You can stop using Hexponents at any time. To delete your account, email{' '}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>. We may suspend or
        terminate accounts that violate these terms. If we terminate your account
        for cause, we are not obligated to refund unused subscription time.
      </p>

      <h2>Changes to these terms</h2>
      <p>
        We may update these terms occasionally. Material changes will be reflected
        in the effective date at the top. Continued use of Hexponents after the
        effective date means you accept the updated terms.
      </p>

      <h2>Governing law</h2>
      <p>
        These terms are governed by the laws of the Commonwealth of Massachusetts,
        without regard to its conflict-of-laws rules. Any dispute arising from these
        terms will be resolved in the state or federal courts located in
        Massachusetts, and you consent to the personal jurisdiction of those courts.
      </p>

      <h2>Contact</h2>
      <p>
        Questions or concerns about these terms? Email{' '}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
      </p>
    </LegalLayout>
  );
}

// Copyright + third-party attributions. Linked from the marketing
// landing footer and (optionally) referenced from App Store Connect's
// Copyright field. The Copyright field expects a short string (e.g.
// "© 2026 DosSantos Systems LLC"), but a public attribution page is
// best-practice for any app shipping open-source dependencies — both
// for license compliance and for App Review transparency.
export function CopyrightPage() {
  return (
    <LegalLayout title="Copyright">
      <p>
        Hexponents and the ⬢ wordmark are copyright © {new Date().getFullYear()}{' '}
        {COMPANY}. All rights reserved.
      </p>

      <h2>Trademarks</h2>
      <p>
        "Hexponents" is an unregistered trademark of {COMPANY}. Apple, the
        Apple logo, App Store, and iPhone are trademarks of Apple Inc.,
        registered in the U.S. and other countries. Use of the App Store
        badge on this site is in accordance with the{' '}
        <a
          href="https://developer.apple.com/app-store/marketing/guidelines/"
          target="_blank"
          rel="noopener noreferrer"
        >
          Apple App Store Marketing Guidelines
        </a>
        . Stripe, Google, SendGrid, and Sentry are trademarks of their
        respective owners.
      </p>

      <h2>Third-party software</h2>
      <p>
        Hexponents is built with open-source software. The full
        dependency list and per-package licenses are available on
        request — email{' '}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>. Notable
        components and their licenses:
      </p>
      <ul>
        <li>React, React DOM — MIT, © Meta Platforms, Inc. and affiliates.</li>
        <li>Vite — MIT, © Yuxi (Evan) You and Vite contributors.</li>
        <li>Express — MIT, © OpenJS Foundation and contributors.</li>
        <li>Prisma — Apache 2.0, © Prisma Data, Inc.</li>
        <li>jsonwebtoken — MIT, © Auth0, Inc.</li>
        <li>bcrypt — MIT.</li>
        <li>bad-words — MIT.</li>
        <li>Inter Tight typeface — SIL Open Font License 1.1, © Rasmus Andersson.</li>
        <li>IBM Plex Mono typeface — SIL Open Font License 1.1, © IBM Corp.</li>
        <li>Sentry — BSL 1.1, © Functional Software, Inc.</li>
      </ul>

      <h2>Reporting copyright concerns</h2>
      <p>
        If you believe content on Hexponents (a custom puzzle, display
        name, or any other user-generated content) infringes your
        copyright, send a written notice including:
      </p>
      <ul>
        <li>Identification of the copyrighted work claimed to be infringed.</li>
        <li>A URL or other description sufficient for us to locate the material.</li>
        <li>Your contact information (name, address, telephone, email).</li>
        <li>
          A statement that you have a good-faith belief that the
          disputed use is not authorized by the copyright owner, its
          agent, or the law.
        </li>
        <li>
          A statement, under penalty of perjury, that the information
          in the notice is accurate and that you are the copyright
          owner or authorized to act on the owner's behalf.
        </li>
        <li>Your physical or electronic signature.</li>
      </ul>
      <p>
        Send notices to <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>{' '}
        with the subject line "Copyright Notice". We respond to valid
        notices promptly and may remove or restrict access to the
        reported material pending review.
      </p>

      <h2>Counter-notice</h2>
      <p>
        If your content was removed and you believe it was a mistake or
        misidentification, you may submit a counter-notice to the same
        address.
      </p>

      <h2>Contact</h2>
      <p>
        For all other copyright questions, email{' '}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
      </p>
    </LegalLayout>
  );
}

// Public support page. Linked from Apple App Store Connect's "Support
// URL" field — Apple Review prefers a real /support URL over the
// homepage. Same chrome as the legal pages so it inherits the brand
// system without extra CSS. Content is plain HTML; no game state, no
// auth, no network — accessible even when the API is down.
export function SupportPage() {
  return (
    <LegalLayout title="Support">
      <p>
        Hexponents is a daily logic puzzle on a hex grid. If something is
        broken, confusing, or you just have a question, this page is the
        place to start. Most issues are answered below — if not, email{' '}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> and we'll
        get back to you within a couple of business days.
      </p>

      <h2>Getting started</h2>
      <ul>
        <li>
          <strong>How do I play?</strong> Open the app and tap the in-game
          tutorial in the menu — it walks through every mechanic in about
          three minutes. Each puzzle gives you a bag of numbered tiles
          and a hex grid where every line carries a constraint (a sum, a
          product, "all even", etc.). Place every tile so every line's
          constraint is satisfied. There's exactly one solution per puzzle.
        </li>
        <li>
          <strong>How often do new puzzles release?</strong> One puzzle
          per difficulty (Easy, Medium, Hard, Expert) drops every day at
          midnight Eastern.
        </li>
        <li>
          <strong>What's a streak?</strong> The flame icon counts how many
          days in a row you've played a puzzle — not how many you've
          solved. Open the app, change at least one tile, and the day
          counts.
        </li>
      </ul>

      <h2>Account &amp; sign-in</h2>
      <ul>
        <li>
          <strong>I forgot my password.</strong> On the sign-in screen tap
          "Forgot password?" — we'll email a reset link. The link is good
          for one hour. If it expires, request a new one.
        </li>
        <li>
          <strong>The reset email never arrived.</strong> Check your spam
          folder. If still missing after 10 minutes, email us and we'll
          look into it. We send from <code>noreply@hexponents.com</code>.
        </li>
        <li>
          <strong>Can I change my display name?</strong> Yes. Open your
          profile and tap "Edit name". Names are unique across all players,
          so you may need to try a few.
        </li>
        <li>
          <strong>Can I delete my account?</strong> Yes — Account → Danger
          zone → Delete account, then type the confirmation phrase. This
          permanently removes your solves, streaks, badges, custom puzzles,
          and follows, and cancels any active subscription. It can't be
          undone.
        </li>
      </ul>

      <h2>Premium &amp; subscriptions</h2>
      <ul>
        <li>
          <strong>What does Premium include?</strong> The full puzzle
          archive, an offline downloader (pull a week's worth of puzzles
          for the train), and the in-app custom puzzle designer.
        </li>
        <li>
          <strong>How much does Premium cost?</strong> $1.99/month or
          $19.99/year. Subscriptions auto-renew unless cancelled at least
          24 hours before the period ends.
        </li>
        <li>
          <strong>How do I cancel?</strong> If you subscribed in the iOS
          app: open the App Store, tap your profile picture, then
          Subscriptions → Hexponents → Cancel. If you subscribed on the
          web at hexponents.com: open Account → Subscription → Cancel.
        </li>
        <li>
          <strong>How do I restore my Premium on a new device?</strong>{' '}
          Sign in with the same account. Apple subscriptions can also be
          restored from Account → "Restore purchases" in the app.
        </li>
        <li>
          <strong>I was charged but I'm not Premium.</strong> Email us
          with the date of the charge and the email address on your
          account — we'll check and reconcile. For Apple charges, also
          include the receipt from your <code>@apple.com</code> email.
        </li>
      </ul>

      <h2>Hints</h2>
      <ul>
        <li>
          <strong>How do hints work?</strong> Tap "Reveal" to spend one
          hint and have the next correct tile placed for you. New
          accounts start with three free hints.
        </li>
        <li>
          <strong>Can I buy more?</strong> Yes — when you run out, the app
          offers a 25-pack for $0.99. Hints are tied to your account, so
          they sync across web and iOS.
        </li>
      </ul>

      <h2>Custom puzzles</h2>
      <ul>
        <li>
          <strong>Who can build custom puzzles?</strong> Premium
          subscribers. Open the in-app designer, lay out cells +
          constraints, and publish. Every published puzzle is
          solver-verified before it ships, so unsolvable maps can't go
          live.
        </li>
        <li>
          <strong>Can anyone play published puzzles?</strong> Yes. Once
          published, anyone can play via the share link or the community
          browse feed — Premium isn't required to solve a custom puzzle.
        </li>
        <li>
          <strong>Reporting an inappropriate puzzle.</strong> Use the
          report button on the puzzle page. Puzzles that accumulate
          enough reports are auto-hidden pending moderator review.
        </li>
      </ul>

      <h2>Troubleshooting</h2>
      <ul>
        <li>
          <strong>The app won't load today's puzzle.</strong> Pull down
          to refresh, or close and reopen the app. If it persists,
          check <a href="https://hexponents.com">hexponents.com</a> in a
          browser — if the site loads but the app doesn't, your device
          may be blocking app traffic (VPN, content filter, etc.).
        </li>
        <li>
          <strong>Push notifications stopped working.</strong> iOS
          Settings → Hexponents → Notifications. Confirm "Allow
          Notifications" is on. The app sends one notification per day
          when the new puzzle is live; if you miss several days the
          system may quiet the channel.
        </li>
        <li>
          <strong>Lost progress on a puzzle I was halfway through.</strong>{' '}
          Mid-puzzle progress saves to your account. Sign in on the
          affected device — your progress should reappear. If not,
          email us with the puzzle date and difficulty.
        </li>
      </ul>

      <h2>Bug reports &amp; feedback</h2>
      <p>
        Found a bug or have a feature idea? Email{' '}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> with as
        much detail as you can — device model, iOS version, screenshot
        if visual. We read every message and tend to ship fixes within
        the week for clear-repro bugs.
      </p>

      <h2>Legal</h2>
      <p>
        For privacy questions see the{' '}
        <a href="/privacy">Privacy Policy</a>. For terms of use see the{' '}
        <a href="/terms">Terms of Service</a>.
      </p>
    </LegalLayout>
  );
}
